import { useCallback, useEffect, useState } from 'react';

import {
  HexIcon,
  Tab,
  TabContent,
  Tabs,
  TabsProvider,
  Placeholder as StatusMessage,
  Box,
  tokens,
} from '@cast/design-system';

import { YamlLineWithHighlight } from './YamlLineWithHighlight';
import {
  tabsContainerSx,
  tabsSx,
  yamlPreviewContainerSx,
  yamlPreviewRootSx,
} from './yamlTabStyles';
import type { LineClickHandler, YamlFile } from '../types';

type YamlPreviewSectionProps = {
  yamlFiles: YamlFile[];
  activeManifestId?: string;
  highlightedLines?: number[];
  onLineClick?: LineClickHandler;
};

export const YamlPreviewSection = ({
  yamlFiles,
  activeManifestId,
  highlightedLines = [],
  onLineClick,
}: YamlPreviewSectionProps) => {
  const getActiveFileName = useCallback(() => {
    const activeFile = yamlFiles.find((file) => file.id === activeManifestId);
    return activeFile?.name || yamlFiles[0]?.name || '';
  }, [activeManifestId, yamlFiles]);

  const [currentTab, setCurrentTab] = useState<string>(getActiveFileName);

  useEffect(() => {
    setCurrentTab(getActiveFileName());
  }, [getActiveFileName]);

  if (yamlFiles.length === 0) {
    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: tokens.theme.bg.structural.code }}
      >
        <StatusMessage
          icon={<HexIcon variant="question" size={64} />}
          title="No YAML files available"
          titleProps={{
            variant: 'P12R',
            color: tokens.theme.fg.structural.secondary,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      height="100%"
      overflow="hidden"
      data-testid="yaml-preview-section"
      sx={{
        backgroundColor: tokens.theme.bg.structural.code,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TabsProvider currentTab={currentTab} setCurrentTab={setCurrentTab}>
        <Box sx={tabsContainerSx}>
          <Tabs fullWidth sx={tabsSx}>
            {yamlFiles.map((file) => (
              <Tab key={file.id} value={file.name} label={file.name} />
            ))}
          </Tabs>
        </Box>

        <Box
          flexGrow={1}
          overflow="auto"
          position="relative"
          sx={{ overflowX: 'auto' }}
        >
          {yamlFiles.map((file) => {
            const isActiveManifest = file.id === activeManifestId;

            return (
              <TabContent key={file.id} value={file.name}>
                <Box
                  sx={{ height: '100%', position: 'relative', px: 24, py: 24 }}
                >
                  <YamlLineWithHighlight
                    data={file.data}
                    size="md"
                    testId={`yaml-preview-${file.name}`}
                    showLineNumbers
                    renderMode="raw"
                    defaultHighlightedLineNumbers={file.highlightedLines || []}
                    activeHighlightedLineNumbers={
                      isActiveManifest ? highlightedLines : []
                    }
                    manifestId={file.id}
                    onLineClick={onLineClick}
                    rootSx={yamlPreviewRootSx}
                    containerSx={yamlPreviewContainerSx}
                  />
                </Box>
              </TabContent>
            );
          })}
        </Box>
      </TabsProvider>
    </Box>
  );
};
